/**
 * Cambio de la forma de pago final por el Supervisor Broker (ticket #1245).
 *
 * Muchos clientes eligen en su app la opción equivocada; el supervisor la corrige
 * después de hablar con ellos. La regla vive en la RPC `broker_cambiar_forma_pago`
 * (migración 20261008051847): aquí solo lo que el diálogo necesita para explicar qué
 * va a pasar, validar el motivo y traducir los errores. Sin Supabase, para probarlo
 * solo.
 */
import {
  ETIQUETA_FORMA_PAGO,
  estadoFormaPago,
  type FormaPago,
} from "@/lib/portal-cobranza/accionesFinanciamiento";
import type { NivelBroker } from "@/lib/portal-bancos/broker";
import { esRpcInexistente } from "@/lib/rpcErrors";
import { inferirTipoMedia } from "@/components/ui/media-tipo";
import { TIPOS_ARCHIVO } from "@/components/ui/multi-file-dropzone";

/** A dónde se cambia: una de las tres formas de pago o null ("aún no elige"). */
export type DestinoFormaPago = FormaPago | null;

export interface OpcionFormaPago {
  valor: DestinoFormaPago;
  titulo: string;
  detalle: string;
}

/**
 * Las opciones del diálogo con el nombre que les da el equipo. "Aún no elige" borra
 * la elección: el cliente la vuelve a hacer en su app.
 */
export const OPCIONES_CAMBIO_FORMA_PAGO: OpcionFormaPago[] = [
  {
    valor: "RECURSOS_PROPIOS",
    titulo: "Recursos propios",
    detalle: "Liquida con su dinero, por transferencia.",
  },
  {
    valor: "FINANCIAMIENTO_EXTERNO",
    titulo: ETIQUETA_FORMA_PAGO.FINANCIAMIENTO_EXTERNO,
    detalle: "Crédito externo: ya lo tiene aprobado con otro banco; SOZU lo registra con él. Sube la carta o una captura como evidencia.",
  },
  {
    valor: "CREDITO_HIPOTECARIO",
    titulo: "Solicitar crédito hipotecario",
    detalle: "Completa su solicitud en su app y el broker se la gestiona con los bancos con convenio.",
  },
  {
    valor: null,
    titulo: "Aún no elige",
    detalle: "Se borra su elección y la vuelve a hacer en su app.",
  },
];

/** Etiqueta de un destino (o de lo que hay hoy) para avisos y resúmenes. */
export function etiquetaDestino(valor: string | null | undefined): string {
  const estado = estadoFormaPago(valor);
  return estado === "sin_definir" ? "Aún no elige" : ETIQUETA_FORMA_PAGO[estado];
}

/** ¿Ese destino es lo que ya tiene? (vacío cuenta como "aún no elige"). */
export function esFormaPagoActual(actual: string | null | undefined, destino: DestinoFormaPago): boolean {
  const estado = estadoFormaPago(actual);
  return destino == null ? estado === "sin_definir" : estado === destino;
}

// ── Motivo ────────────────────────────────────────────────────────────────────
export const MOTIVO_MIN_CAMBIO_FORMA_PAGO = 10;
export const MOTIVO_MAX_CAMBIO_FORMA_PAGO = 1000;

/** `valor` = el motivo sin espacios en los extremos; `error` vacío cuando es válido. */
export interface ValidacionMotivo {
  ok: boolean;
  valor: string;
  error: string;
}

/** Mismas reglas que la RPC: de 10 a 1000 caracteres sin contar los espacios de los extremos. */
export function validarMotivoCambioFormaPago(motivo: string | null | undefined): ValidacionMotivo {
  const valor = (motivo ?? "").trim();
  if (valor.length < MOTIVO_MIN_CAMBIO_FORMA_PAGO) {
    return { ok: false, valor, error: `Escribe el motivo del cambio (mínimo ${MOTIVO_MIN_CAMBIO_FORMA_PAGO} caracteres).` };
  }
  if (valor.length > MOTIVO_MAX_CAMBIO_FORMA_PAGO) {
    return { ok: false, valor, error: `El motivo es demasiado largo (máximo ${MOTIVO_MAX_CAMBIO_FORMA_PAGO} caracteres).` };
  }
  return { ok: true, valor, error: "" };
}

// ── Quién ─────────────────────────────────────────────────────────────────────
export interface ActorCambioFormaPago {
  /** Super Administrador (rol_id 1). */
  esSuperAdmin: boolean;
  /** Nivel de su rol ACTIVO en el broker; null = otro rol. */
  nivel: NivelBroker | null;
  /** `usuarios.id_banco`: un usuario de banco nunca. */
  idBanco: number | null | undefined;
  /** Permiso leer en /admin/portal-bancos/clientes (revocable desde Roles y Permisos). */
  puedeLeerBroker: boolean;
}

/** Mismo gate que la RPC: Super Admin, o Supervisor Broker sin banco y con leer en el broker. */
export function puedeCambiarFormaPago(a: ActorCambioFormaPago): boolean {
  if (a.esSuperAdmin) return true;
  return a.nivel === "supervisor" && a.idBanco == null && a.puedeLeerBroker;
}

// ── Qué va a pasar ────────────────────────────────────────────────────────────
/** Lo que el diálogo sabe de las solicitudes de la cuenta (el broker solo ve las suyas). */
export interface SolicitudDeCuenta {
  folio: string;
  estatus: string;
  canal?: string | null;
}

/** Etapas en que el banco todavía no aprueba: el broker puede cerrar la solicitud. */
export const ETAPAS_ANTES_DE_APROBAR = ["nuevo", "asignado", "contactado", "en_evaluacion"];
/** Cierres sin crédito: no cuentan como solicitud viva. */
export const CIERRES_SIN_CREDITO = ["rechazado", "desistido", "expirada"];

export const MENSAJE_CREDITO_APROBADO =
  "El crédito ya fue aprobado por el banco; ciérralo desde Gestión antes de cambiar la forma de pago";
export const MENSAJE_CREDITO_FORMALIZADO =
  "El cliente ya tiene un crédito formalizado con el banco; la forma de pago ya no se cambia desde el broker";

/** Lo que pasa al cambiar a crédito cuando el cliente aún no tiene solicitud. */
export const EFECTO_CREDITO_SIN_SOLICITUD =
  "Se cambia la forma de pago. El cliente recibirá un aviso para completar su solicitud de crédito en su app "
  + "(monto, plazo y su autorización para compartir sus datos con el banco). La solicitud llegará aquí cuando la envíe.";

export interface ConsecuenciasCambio {
  /** Por qué no se puede (el botón se apaga); null = se puede. */
  bloqueo: string | null;
  /** Lo que va a pasar, en el orden en que se lee. */
  efectos: string[];
}

const lista = (folios: string[]) =>
  folios.length <= 1 ? folios.join("") : `${folios.slice(0, -1).join(", ")} y ${folios[folios.length - 1]}`;

/**
 * Lo que hará la RPC con esta cuenta, para que el supervisor lo lea antes de confirmar.
 * Si la lista de solicitudes no trae alguna (las del canal directo no le llegan al
 * broker), la RPC tiene la última palabra.
 */
export function consecuenciasCambioFormaPago(e: {
  actual: string | null | undefined;
  destino: DestinoFormaPago;
  solicitudes: SolicitudDeCuenta[];
}): ConsecuenciasCambio {
  const efectos: string[] = [];
  const actual = estadoFormaPago(e.actual);

  if (e.destino !== "CREDITO_HIPOTECARIO") {
    const conCredito = e.solicitudes.find(
      (s) => !ETAPAS_ANTES_DE_APROBAR.includes(s.estatus) && !CIERRES_SIN_CREDITO.includes(s.estatus),
    );
    if (conCredito) {
      const formalizado = conCredito.estatus === "formalizado" || conCredito.estatus === "atendido_fuera";
      return { bloqueo: `${formalizado ? MENSAJE_CREDITO_FORMALIZADO : MENSAJE_CREDITO_APROBADO} (${conCredito.folio}).`, efectos };
    }
    const directa = e.solicitudes.find(
      (s) => s.canal != null && s.canal !== "broker" && ETAPAS_ANTES_DE_APROBAR.includes(s.estatus),
    );
    if (directa) {
      return {
        bloqueo: `La cuenta tiene una solicitud directa (${directa.folio}) que lleva el banco; pide que la cierren antes de cambiar la forma de pago.`,
        efectos,
      };
    }
    const vivas = e.solicitudes.filter((s) => ETAPAS_ANTES_DE_APROBAR.includes(s.estatus)).map((s) => s.folio);
    if (vivas.length) {
      efectos.push(
        vivas.length === 1
          ? `La solicitud ${vivas[0]} se cierra como Desistida con tu motivo; queda en su Bitácora.`
          : `Las solicitudes ${lista(vivas)} se cierran como Desistidas con tu motivo; queda en su Bitácora.`,
      );
    }
    if (actual === "CREDITO_HIPOTECARIO") {
      efectos.push("Se da de baja su crédito hipotecario pendiente.");
    }
    if (e.destino == null) {
      efectos.push("Vuelve a elegir su forma de pago en su app y regresa a la lista de Clientes.");
    } else if (e.destino === "FINANCIAMIENTO_EXTERNO") {
      efectos.push(`Queda en la lista de Clientes como «${ETIQUETA_FORMA_PAGO.FINANCIAMIENTO_EXTERNO}» para registrar su crédito.`);
    } else {
      efectos.push("Pasa a la pestaña «Recursos propios» de Clientes: ya no se le contacta.");
    }
  } else {
    const lleva = e.solicitudes.find((s) => !CIERRES_SIN_CREDITO.includes(s.estatus));
    if (lleva) {
      efectos.push(`Se conserva su solicitud ${lleva.folio}.`);
    } else {
      // La solicitud no la crea el broker: la manda el cliente desde su app, con su
      // autorización para compartir sus datos (decisión del dueño del producto).
      efectos.push(EFECTO_CREDITO_SIN_SOLICITUD);
      efectos.push("Mientras tanto ya no aparece en la lista de Clientes.");
    }
  }

  efectos.push("Aplica a la propiedad y a su estacionamiento y bodega. No mueve pagos ni acuerdos.");
  efectos.push("Queda en el historial con tu nombre y el motivo.");
  return { bloqueo: null, efectos };
}

// ── Respuesta y errores de la RPC ─────────────────────────────────────────────
/** Lo que devuelve `broker_cambiar_forma_pago`. */
export interface ResultadoCambioFormaPago {
  id_cuenta_principal: number;
  cuentas: number[];
  cuentas_actualizadas: number[];
  tipo_anterior: string | null;
  tipo_nuevo: string | null;
  solicitudes_cerradas: number[];
  /** La RPC ya no crea solicitudes (la manda el cliente): siempre null. */
  solicitud_creada: number | null;
  solicitud_conservada: number | null;
  creditos_dados_de_baja: number[];
  id_credito: number | null;
  /**
   * Al pasar a crédito sin solicitud: se le mandó al cliente el aviso para completarla.
   * false si no tiene compradores con correo o si el aviso falló (el cambio se guarda igual).
   */
  aviso_cliente_enviado?: boolean | null;
}

/** Folio con el que el portal nombra una solicitud (SOL-00012). */
export const folioSolicitud = (id: number | string) => `SOL-${String(id).padStart(5, "0")}`;

/** Aviso de éxito: la forma de pago nueva y lo que pasó con las solicitudes. */
export function resumenCambioFormaPago(r: ResultadoCambioFormaPago): string {
  const partes = [`Forma de pago: ${etiquetaDestino(r.tipo_nuevo)}.`];
  const cerradas = (r.solicitudes_cerradas ?? []).map(folioSolicitud);
  if (cerradas.length === 1) partes.push(`Se cerró la solicitud ${cerradas[0]}.`);
  if (cerradas.length > 1) partes.push(`Se cerraron las solicitudes ${lista(cerradas)}.`);
  if (r.solicitud_conservada != null) {
    partes.push(`Se conservó la solicitud ${folioSolicitud(r.solicitud_conservada)}.`);
  } else if (r.tipo_nuevo === "CREDITO_HIPOTECARIO") {
    partes.push(
      r.aviso_cliente_enviado
        ? "Le avisamos al cliente para que complete su solicitud en su app; llegará aquí cuando la envíe."
        : "No se pudo avisar al cliente: pídele que entre a su app para completar su solicitud.",
    );
  }
  return partes.join(" ");
}

/**
 * Aviso (toast) al terminar: título corto y una descripción que dice de qué a qué cambió y,
 * después, lo que pasó con solicitudes o el aviso al cliente. `atencion` = el supervisor tiene
 * algo pendiente (pedirle al cliente que complete su solicitud porque no se le pudo avisar).
 */
export function avisoCambioFormaPago(r: ResultadoCambioFormaPago): { titulo: string; descripcion: string; atencion: boolean } {
  const atencion = r.tipo_nuevo === "CREDITO_HIPOTECARIO" && r.solicitud_conservada == null && !r.aviso_cliente_enviado;
  const resto = resumenCambioFormaPago(r).replace(/^Forma de pago: [^.]*\.\s*/, "");
  const cambio = `Pasó de ${etiquetaDestino(r.tipo_anterior)} a ${etiquetaDestino(r.tipo_nuevo)}.`;
  // Sin solicitud todavía sale en Abiertas como cliente, no como solicitud (#1298).
  const donde = r.tipo_nuevo === "CREDITO_HIPOTECARIO" && r.solicitud_conservada == null
    ? " Mientras, la ves en Solicitudes › Abiertas, marcada «Falta su solicitud»."
    : "";
  return {
    titulo: atencion ? "Forma de pago actualizada, falta avisar al cliente" : "Forma de pago actualizada",
    descripcion: (resto ? `${cambio} ${resto}` : cambio) + donde,
    atencion,
  };
}

/** La RPC todavía no existe en este ambiente: se esconde la acción. */
export const cambioFormaPagoNoDisponible = (error: unknown): boolean => {
  const code = (error as { code?: string } | null)?.code;
  // Los errores de negocio de la RPC traen su código: nunca son "no existe".
  if (code === "22023" || code === "FP001" || code === "FP002" || code === "42501" || code === "28000") return false;
  return esRpcInexistente(error);
};

/** Error de la RPC en palabras para el supervisor. */
export function mensajeErrorCambioFormaPago(error: unknown): string {
  const { code, message } = (error ?? {}) as { code?: string; message?: string };
  const texto = (message ?? "").trim();
  switch (code) {
    case "42501":
      return "No tienes permiso para cambiar la forma de pago: solo el Supervisor Broker puede hacerlo.";
    case "28000":
      return "Tu sesión expiró. Vuelve a entrar e inténtalo de nuevo.";
    case "FP001":
      return texto || MENSAJE_CREDITO_APROBADO;
    case "FP002":
      return texto || "La cuenta tiene una solicitud directa que lleva el banco; pide que la cierren antes de cambiar la forma de pago.";
    case "22023":
      return texto || "Revisa los datos del cambio.";
    case "23505":
      return "La cuenta ya tiene una solicitud de crédito abierta. Recarga la ficha e inténtalo de nuevo.";
  }
  if (cambioFormaPagoNoDisponible(error)) {
    return "El cambio de forma de pago todavía no está disponible en este ambiente.";
  }
  return "No se pudo cambiar la forma de pago. Intenta de nuevo.";
}

// ── Evidencia (opcional) ──────────────────────────────────────────────────────
/**
 * Evidencia del cambio: foto, video, documento o audio (carta del banco, captura de la app,
 * nota de voz…), con el mismo clip del chat. Es opcional; el motivo sigue siendo obligatorio.
 * La sube el front al bucket `documentos` bajo `broker-forma-pago/<id_cuenta>/` y la guarda
 * la RPC `broker_cambiar_forma_pago_con_evidencia` (migración 20261013214713) en el registro
 * del cambio.
 */
export const MAX_EVIDENCIAS_FORMA_PAGO = 5;
export const MAX_EVIDENCIA_FORMA_PAGO_MB = 10;
/** Video y audio pesan más: tope propio. */
export const MAX_EVIDENCIA_MEDIA_FORMA_PAGO_MB = 50;
/** Las opciones del clip (Foto, Video, Documento, Audio), las mismas del chat. */
export const OPCIONES_EVIDENCIA_FORMA_PAGO = TIPOS_ARCHIVO;

export interface EvidenciaFormaPago {
  path: string;
  nombre: string;
  mime: string | null;
}

const EXT_DOCUMENTO = /\.(pdf|docx?|xlsx?|pptx?|txt|csv)$/i;
const MIME_DOCUMENTO = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/plain",
  "text/csv",
]);

/** ¿Es foto, video, audio o un documento (PDF, Office, texto)? Por mime y, si no trae, por extensión. */
export function esEvidenciaPermitida(archivo: { name: string; type?: string | null }): boolean {
  const mime = (archivo.type ?? "").toLowerCase();
  if (inferirTipoMedia(mime, archivo.name) !== "documento") return true;
  return MIME_DOCUMENTO.has(mime) || EXT_DOCUMENTO.test(archivo.name);
}

const topeMb = (a: { name: string; type?: string | null }) => {
  const t = inferirTipoMedia(a.type, a.name);
  return t === "video" || t === "audio" ? MAX_EVIDENCIA_MEDIA_FORMA_PAGO_MB : MAX_EVIDENCIA_FORMA_PAGO_MB;
};

/** Error para el usuario o null si los archivos se pueden adjuntar. */
export function validarEvidenciasFormaPago(archivos: Array<{ name: string; type?: string | null; size: number }>): string | null {
  if (archivos.length > MAX_EVIDENCIAS_FORMA_PAGO) {
    return `Puedes adjuntar hasta ${MAX_EVIDENCIAS_FORMA_PAGO} archivos.`;
  }
  const otro = archivos.find((a) => !esEvidenciaPermitida(a));
  if (otro) return `«${otro.name}» no es foto, video, audio ni documento.`;
  const pesado = archivos.find((a) => a.size > topeMb(a) * 1024 * 1024);
  if (pesado) return `«${pesado.name}» pesa más de ${topeMb(pesado)} MB.`;
  return null;
}

/** Ruta en el bucket: la RPC solo acepta `broker-forma-pago/<id_cuenta>/...`. */
export function rutaEvidenciaFormaPago(idCuenta: number, nombreSeguro: string, uuid: string): string {
  return `broker-forma-pago/${idCuenta}/${uuid}-${nombreSeguro}`;
}

/** Un cambio del historial (`broker_historial_forma_pago`), para la bitácora de la ficha. */
export interface CambioFormaPagoHistorial {
  id: number;
  fecha: string;
  autor: string;
  tipoAnterior: string | null;
  tipoNuevo: string | null;
  motivo: string | null;
  evidencia: EvidenciaFormaPago[];
}

export function normalizarHistorialFormaPago(rows: any[] | null | undefined): CambioFormaPagoHistorial[] {
  return (rows ?? [])
    .filter((r) => r && r.id != null && r.fecha)
    .map((r) => ({
      id: Number(r.id),
      fecha: String(r.fecha),
      autor: String(r.autor_nombre || r.autor_email || "Usuario"),
      tipoAnterior: r.tipo_anterior ?? null,
      tipoNuevo: r.tipo_nuevo ?? null,
      motivo: r.motivo ?? null,
      evidencia: Array.isArray(r.evidencia)
        ? r.evidencia
            .filter((e: any) => e && typeof e.path === "string" && e.path)
            .map((e: any) => ({ path: e.path, nombre: e.nombre || "Evidencia", mime: e.mime ?? null }))
        : [],
    }));
}
